import { Component, Input, Output, EventEmitter } from '@angular/core';

import { ButtonComponent } from '../../atoms/button/button.component';

/**
 * Full-screen fatal-error screen shown when the app shell loaded but a
 * crucial bootstrap dependency failed — e.g. auth/OIDC discovery or config
 * couldn't start, so Angular is running yet there is nothing usable to show.
 *
 * Distinct from the two existing error surfaces: `EmptyStateComponent` is an
 * in-page "no data" placeholder, and the toast interceptor reports transient,
 * recoverable API errors. This takes over the whole viewport and offers a
 * single retry path back into the app.
 */
@Component({
  selector: 'es-app-error',
  imports: [ButtonComponent],
  template: `
    <div class="min-h-screen flex items-center justify-center p-6 bg-[var(--bg)]">
      <div class="max-w-md w-full text-center">
        <span class="text-5xl block mb-4">⚠️</span>
        <h1 class="text-xl font-semibold text-[var(--text)]">{{ title }}</h1>
        <p class="text-sm text-[var(--text-secondary)] mt-2">{{ message }}</p>

        @if (detail) {
          <details class="mt-4 text-left">
            <summary class="text-xs text-[var(--text-secondary)] cursor-pointer select-none">
              Technical details
            </summary>
            <pre class="mt-2 p-3 rounded bg-[var(--bg-secondary)] text-xs text-[var(--text-secondary)] whitespace-pre-wrap break-words">{{ detail }}</pre>
          </details>
        }

        <es-button class="mt-6 inline-block" variant="primary" (clicked)="retry.emit()">
          {{ retryLabel }}
        </es-button>
      </div>
    </div>
  `,
})
export class AppErrorComponent {
  @Input() title = 'Something went wrong';
  @Input() message = "We couldn't start the app. This is usually temporary — please try again.";
  /** Optional technical error text, tucked behind a collapsed disclosure. */
  @Input() detail?: string;
  @Input() retryLabel = 'Try again';
  @Output() retry = new EventEmitter<void>();
}
